import type * as PixiNS from 'pixi.js';
import { isLocked, isShown } from '../domain/sceneOps';
import { NEUTRAL_POSE, type Animation, type Character, type CharacterPose, type FillGradient, type Scene, type SceneElement, type SceneObject } from '../domain/schemas';
import { debugCounters } from '../lib/debug';
import { Observable } from '../lib/store';
import { characterParts, characterRig, isNeutralPose, segmentRuns, segmentTransforms, type Rig, type Segment } from '../scene/characters';
import { HANDLE_FRACTIONS, ROTATE_OFFSET, cornersOf, localToScene, type Camera } from '../scene/geometry';
import { VIGNETTE_RADIUS, gradeMatrix, isNeutralGrade, isVisibleVignette, linearPoints, stopColor, vignetteStops } from '../scene/look';
import { PlaybackClock, type ClockSample, type FrameClock } from './clock';
import { hexToNumber, poseAt } from './motion';
import { displayObject, evaluateAnimation, type ChannelValues, type DisplayObject, type EvaluatedState } from './timeline';
import { composeObject, type ReactionFrame } from '../av/reactions';
import type { VisualizerFrame } from '../visualizer/evaluate';
import { VisualizerRenderer } from '../visualizer/render';

/** Phase 4A: audio-reactive overlays at a session time (pure; evaluated every displayed frame). */
export type Reactor = (seconds: number) => ReactionFrame | null;

/** Phase 4C: music visualizer layers at a session time (pure; evaluated every displayed frame). */
export type VisualizerSource = (seconds: number) => VisualizerFrame | null;

export type PixiModule = typeof PixiNS;

export type AnimationStatus = 'idle' | 'loading' | 'paused' | 'running' | 'error' | 'destroyed';

export interface AnimationSnapshot {
  status: AnimationStatus;
  frame: number;
  elapsed: number;
  error?: string;
  /** Image assets that could not be loaded (drawn as a placeholder, never silently). */
  imageErrors?: string[];
}

export async function loadPixi(): Promise<PixiModule> {
  const pixi = await import('pixi.js');
  // Our production CSP forbids eval; this registers Pixi's eval-free shader/uniform paths.
  await import('pixi.js/unsafe-eval');
  return pixi;
}

/** Loads a stored image asset as a decoded bitmap (injected; reads IndexedDB in the app). */
export type ImageLoader = (assetId: string) => Promise<ImageBitmap>;

export interface RendererOptions {
  loadImage?: ImageLoader;
  /** Budget for decoded image textures (width × height × 4 bytes). */
  textureBudgetBytes?: number;
  /** Called after the viewport is resized (the editor refits its camera). */
  onResize?: (width: number, height: number) => void;
  /** The clock to follow: the shared animation clock, or a Phase 4A session clock. */
  clock?: FrameClock;
  /** Device-pixel resolution cap (the Sync preview uses 1 to stay light on modest hardware). */
  maxResolution?: number;
  /**
   * Phase 4B video export: a fixed-size surface at resolution 1 (output pixels, not device
   * pixels), with no resize observer, no editor chrome (selection overlay, guides,
   * outside-of-scene dimming) and a black letterbox. Frames are drawn on demand only.
   */
  exportSize?: { width: number; height: number };
  /**
   * Phase 5B.1: rendering quality of glow, shadow and grade filters. `draft` renders them
   * at half resolution with fewer blur passes (lighter previews); `full` (default) is what
   * the export uses.
   */
  effectsQuality?: EffectsQuality;
}

export type EffectsQuality = 'full' | 'draft';

/** Blur passes per quality (Pixi BlurFilter `quality`). */
/** Side of the radial gradient textures (Pixi's own default); GPU filtering smooths between texels. */
const RADIAL_TEXTURE_SIZE = 256;
const BLUR_QUALITY: Record<EffectsQuality, number> = { full: 4, draft: 2 };
/** Filter resolution per quality ('inherit' = the render target's). */
const FILTER_RESOLUTION: Record<EffectsQuality, number | 'inherit'> = { full: 'inherit', draft: 0.5 };

export const DEFAULT_TEXTURE_BUDGET = 64 * 1024 * 1024;

interface ObjectNode {
  object: SceneObject;
  /** Key of everything that affects drawing (not position/rotation/opacity/pose). */
  drawKey: string;
  node: PixiNS.Container;
  content: PixiNS.Container;
  /** Characters: one Graphics per run of same-segment parts, posed by transform only. */
  segments?: { segment: Segment; graphic: PixiNS.Graphics }[];
  rig?: Rig;
  /** Pose currently applied to the segments (to skip unchanged poses). */
  poseKey?: string;
  /** A reaction tint is applied (Phase 4A). */
  tinted?: boolean;
  /** Phase 5B.1: gradient fills owned by this node's drawing (destroyed with it). */
  gradients?: PixiNS.FillGradient[];
  /** Phase 5B.1: glow and shadow copies of the drawing, behind `content`. */
  effects?: EffectCopy[];
}

/** A solid-colour copy of an object's drawing (its silhouette), blurred, behind the object. */
interface EffectCopy {
  kind: 'glow' | 'shadow';
  container: PixiNS.Container;
  /** Shared blur filter key (null: an unblurred hard shadow). */
  blurKey: string | null;
  segments?: { segment: Segment; graphic: PixiNS.Graphics }[];
  offsetX: number;
  offsetY: number;
}

/** A blur filter shared by every effect copy with the same radius (reference-counted). */
interface SharedBlur {
  filter: PixiNS.BlurFilter;
  radius: number;
  refs: number;
}

interface CachedTexture {
  texture: PixiNS.Texture;
  bitmap: ImageBitmap;
  bytes: number;
}

export interface RenderStats {
  objects: number;
  textures: number;
  textureBytes: number;
  redraws: number;
  transformUpdates: number;
  /** Keyframe evaluations (one per displayed animation frame). */
  evaluations: number;
  /** Character pose updates (transform-only; never redraw). */
  poseUpdates: number;
  /** Total time spent evaluating keyframes, in ms. */
  evaluationMs: number;
  frame: number;
  /** Motion Composer preview guides (paths and trails) and their points. */
  guides: number;
  guidePoints: number;
  /** Phase 4A: reaction evaluations, their total cost (evaluate + apply, ms) and objects currently moved by reactions. */
  reactionEvaluations: number;
  reactionMs: number;
  reactingObjects: number;
  /** Phase 4C: visualizer evaluations, their total cost (evaluate + draw, ms), live layers and shapes drawn in the last frame. */
  visualizerEvaluations: number;
  visualizerMs: number;
  visualizerLayers: number;
  visualizerElements: number;
  /** Phase 5B.1: live shared blur filters, objects drawing a glow or shadow, gradient fills, and the scene finish. */
  effectFilters: number;
  effectObjects: number;
  gradients: number;
  grade: boolean;
  vignette: boolean;
}

/** A preview polyline drawn over the scene (Motion Composer paths and trajectories). */
export interface Guide {
  points: readonly (readonly [number, number])[];
  kind: 'path' | 'trail';
}

/** Properties that only move/fade a node; changing them never redraws its content. */
/**
 * Destroy options for our own display objects. Pixi 8 only frees a Graphics' own
 * GraphicsContext when `context` is set (or no options are passed); without it the
 * renderer keeps every context's GPU data cached, which leaked on each scene switch.
 * Textures are never destroyed here: image textures are owned by the texture cache.
 */
const NODE_DESTROY = { children: true, context: true, texture: false, textureSource: false } as const;

const TRANSFORM_ONLY = new Set(['x', 'y', 'rotation', 'opacity', 'anchorX', 'anchorY', 'visible', 'locked', 'name', 'groupId', 'layerId', 'pose']);

function drawKeyOf(o: SceneObject, characters: ReadonlyMap<string, Character>): string {
  const parts: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) if (!TRANSFORM_ONLY.has(k)) parts[k] = v;
  if (o.type === 'character') parts.def = characters.get(o.characterId) ?? null;
  return JSON.stringify(parts);
}

/** True when a container already holds exactly these children, in this order. */
function sameChildren(parent: PixiNS.Container, children: readonly PixiNS.Container[]): boolean {
  return parent.children.length === children.length && children.every((c, i) => parent.children[i] === c);
}

/** White blended toward a colour by `strength` (a tint of 0xffffff leaves a node unchanged). */
function mixWhite(color: number, strength: number): number {
  const k = Math.min(1, Math.max(0, strength));
  const ch = (shift: number) => Math.round(255 + (((color >> shift) & 0xff) - 255) * k);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

const FONTS: Record<string, string> = { sans: 'system-ui, sans-serif', serif: 'Georgia, serif', mono: 'ui-monospace, monospace' };

/**
 * Owns a single PixiJS Application bound to a container element and renders a scene into
 * it: background (colour, gradient, image), ordered layers of scene objects, procedural
 * characters, the Phase 0 animated shapes, and a selection overlay. Scene data stays plain
 * data; Pixi objects are derived from it.
 *
 * Guarantees:
 *  - at most one Application per controller; everything created (canvas, GPU textures,
 *    gradients, text textures, ticker callbacks, ResizeObserver) is destroyed in
 *    `destroy()`, also when destroy races an in-flight `mount()`;
 *  - updates are incremental and keyed by object id: a change to position, rotation,
 *    opacity, visibility or layer only updates the node; a style, size or type change
 *    redraws that one object; removed objects are destroyed;
 *  - image textures are decoded lazily, shared per asset, and kept within a byte budget
 *    (least recently used textures not shown in the current scene are destroyed first);
 *  - frames are rendered on demand (after a change), not continuously, unless the
 *    clock is playing;
 *  - animation is applied non-destructively: the controller follows the shared
 *    PlaybackClock, evaluates the selected animation at the displayed frame (only when
 *    the frame changes) and applies the values on top of the base objects. Project data
 *    is never modified. There is one ticker; Phase 0 shapes use the same clock time.
 */
export class AnimationController {
  readonly state: Observable<AnimationSnapshot>;
  private app: PixiNS.Application | null = null;
  private pixi: PixiModule | null = null;
  private scene: Scene | null = null;
  private characters = new Map<string, Character>();
  private camera: Camera = { zoom: 1, panX: 0, panY: 0 };
  private selection: string[] = [];
  private preview = new Map<string, Partial<SceneObject>>();
  private resizeObserver: ResizeObserver | null = null;
  private destroyed = false;
  private readonly load: () => Promise<PixiModule>;
  private readonly options: RendererOptions;

  private world: PixiNS.Container | null = null;
  /** Phase 5B.1: everything the colour grade applies to (backdrop, layers, visualizer, Phase 0 shapes). */
  private sceneRoot: PixiNS.Container | null = null;
  private gradeFilter: PixiNS.ColorMatrixFilter | null = null;
  private vignette: PixiNS.Graphics | null = null;
  private vignetteGradient: PixiNS.FillGradient | null = null;
  private blurs = new Map<string, SharedBlur>();
  /** Each layer container's own default blend mode (restored when a layer goes back to normal). */
  private layerBaseBlend = new WeakMap<PixiNS.Container, PixiNS.BLEND_MODES>();
  private effectsQuality: EffectsQuality;
  private backdrop: PixiNS.Graphics | null = null;
  private gradient: PixiNS.FillGradient | null = null;
  private backgroundSprite: PixiNS.Sprite | null = null;
  private backgroundMask: PixiNS.Graphics | null = null;
  private layersRoot: PixiNS.Container | null = null;
  private layerNodes = new Map<string, PixiNS.Container>();
  private nodes = new Map<string, ObjectNode>();
  private animated: PixiNS.Container | null = null;
  private sprites: { element: SceneElement; graphic: PixiNS.Graphics }[] = [];
  private outside: PixiNS.Graphics | null = null;
  private shownSeconds = 0;
  private overlay: PixiNS.Graphics | null = null;
  private guideGraphic: PixiNS.Graphics | null = null;
  private guides: readonly Guide[] = [];
  private textures = new Map<string, CachedTexture>();
  private loading = new Map<string, Promise<void>>();
  private imageErrors = new Set<string>();
  private counters = { redraws: 0, transformUpdates: 0, evaluations: 0, poseUpdates: 0, evaluationMs: 0, reactionEvaluations: 0, reactionMs: 0, visualizerEvaluations: 0, visualizerMs: 0 };
  private reactor: Reactor | null = null;
  private visualizer: VisualizerSource | null = null;
  private visualizerFrame: VisualizerFrame | null = null;
  private vizBehind: PixiNS.Container | null = null;
  private vizFront: PixiNS.Container | null = null;
  private vizRenderer: VisualizerRenderer | null = null;
  private reactions: ReactionFrame | null = null;
  private flashGraphic: PixiNS.Graphics | null = null;
  private pulseGraphic: PixiNS.Graphics | null = null;
  private effectKey = '';
  private readonly clock: FrameClock;
  private unsubscribeClock: (() => void) | null = null;
  private animation: Animation | null = null;
  private evaluated: Map<string, ChannelValues> = new Map();
  private shownFrame = -1;

  constructor(
    load: () => Promise<PixiModule> = loadPixi,
    state = new Observable<AnimationSnapshot>({ status: 'idle', frame: 0, elapsed: 0 }),
    options: RendererOptions = {},
  ) {
    this.load = load;
    this.state = state;
    this.options = options;
    this.effectsQuality = options.effectsQuality ?? 'full';
    this.clock = options.clock ?? new PlaybackClock();
    this.state.update({ frame: 0, elapsed: 0 });
  }

  get snapshot(): AnimationSnapshot {
    return this.state.get();
  }

  stats(): RenderStats {
    let textureBytes = 0;
    for (const t of this.textures.values()) textureBytes += t.bytes;
    return {
      objects: this.nodes.size,
      textures: this.textures.size,
      textureBytes,
      ...this.counters,
      frame: this.shownFrame,
      guides: this.guides.length,
      guidePoints: this.guides.reduce((n, g) => n + g.points.length, 0),
      reactingObjects: this.reactions?.objects.size ?? 0,
      visualizerLayers: this.vizRenderer?.stats.layers ?? 0,
      visualizerElements: this.vizRenderer?.stats.elements ?? 0,
      effectFilters: this.blurs.size,
      effectObjects: [...this.nodes.values()].filter((n) => n.effects?.length).length,
      gradients: [...this.nodes.values()].reduce((n, e) => n + (e.gradients?.length ?? 0), 0),
      grade: !!this.gradeFilter,
      vignette: !!this.vignette?.visible,
    };
  }

  /**
   * Phase 4A: audio-reactive overlays. The reactor is evaluated on every displayed frame
   * (envelopes are continuous) and composed on top of the animated values; only reacting
   * nodes are touched, by transform or tint — never redrawn. Null removes every overlay.
   */
  setReactor(reactor: Reactor | null): void {
    this.reactor = reactor;
    if (reactor) this.ensureEffectLayers();
    this.refresh();
  }

  /**
   * Phase 4C: music visualizer layers, drawn every displayed frame into two containers —
   * "behind" (above the background, below every scene object) and "front" (above every
   * scene object, below the Phase 4A flash). Null removes them.
   */
  setVisualizer(source: VisualizerSource | null): void {
    this.visualizer = source;
    if (source) this.ensureVisualizerLayers();
    this.refresh();
  }

  /** The visualizer frame shown last (tests and diagnostics). */
  shownVisualizer(): VisualizerFrame | null {
    return this.visualizerFrame;
  }

  private ensureVisualizerLayers(): void {
    const pixi = this.pixi;
    const root = this.sceneRoot;
    if (!pixi || !root || this.vizRenderer || !this.layersRoot || !this.animated) return;
    this.vizBehind = new pixi.Container();
    this.vizFront = new pixi.Container();
    root.addChildAt(this.vizBehind, root.getChildIndex(this.layersRoot));
    root.addChildAt(this.vizFront, root.getChildIndex(this.animated) + 1);
    this.vizRenderer = new VisualizerRenderer(pixi, this.vizBehind, this.vizFront);
  }

  /**
   * Phase 4A effects: a background pulse above the backdrop and a flash above the scene.
   * Created only for renderers with a reactor, so the editors' display tree is unchanged.
   */
  private ensureEffectLayers(): void {
    const pixi = this.pixi;
    const world = this.world;
    const root = this.sceneRoot;
    if (!pixi || !world || !root || this.pulseGraphic || !this.layersRoot || !this.animated || !this.vignette) return;
    this.pulseGraphic = new pixi.Graphics();
    this.flashGraphic = new pixi.Graphics();
    root.addChildAt(this.pulseGraphic, root.getChildIndex(this.vizBehind ?? this.layersRoot));
    // The flash stays on top of the graded scene and the vignette (it is never graded).
    world.addChildAt(this.flashGraphic, world.getChildIndex(this.vignette) + 1);
  }

  /** Re-applies animation and reactions at the clock's position (after a data change while paused). */
  refresh(): void {
    if (!this.app) return;
    this.showSample(this.clock.sample(), true);
    this.render();
  }

  /** Displayed channel values (animation + reactions) of animated or reacting objects. */
  displayedValues(): Map<string, ChannelValues> {
    const out = new Map<string, ChannelValues>();
    const ids = new Set([...this.evaluated.keys(), ...(this.reactions?.objects.keys() ?? [])]);
    for (const id of ids) {
      const entry = this.nodes.get(id);
      if (!entry) continue;
      const v = composeObject(entry.object, this.evaluated.get(id), this.reactions?.objects.get(id));
      if (v) out.set(id, v);
    }
    return out;
  }

  /** Scene effects currently shown (flash, background pulse, shake). */
  shownEffects(): ReactionFrame['effects'] | null {
    return this.reactions?.effects ?? null;
  }

  /** Animated values at the displayed frame (object id → channel values). */
  evaluatedState(): EvaluatedState {
    return this.evaluated;
  }

  /**
   * Resolves once every image texture requested so far has loaded or failed (Phase 4B
   * export waits for this before the first frame). Returns the asset ids that failed.
   */
  async whenImagesReady(): Promise<string[]> {
    while (this.loading.size > 0) await Promise.all([...this.loading.values()]);
    return [...this.imageErrors];
  }

  /** The canvas frames are drawn to (null before mount or after destroy). */
  get canvas(): HTMLCanvasElement | null {
    return this.app?.canvas ?? null;
  }

  /** Size of the drawing surface in CSS pixels. */
  /** Clock position (seconds) of the frame currently drawn (diagnostics and tests). */
  get shownPosition(): number {
    return this.shownSeconds;
  }

  get viewSize(): { width: number; height: number } {
    return this.app ? { width: this.app.screen.width, height: this.app.screen.height } : { width: 0, height: 0 };
  }

  async mount(container: HTMLElement, scene: Scene, characters: readonly Character[] = []): Promise<void> {
    if (this.app || this.destroyed) return;
    this.scene = scene;
    this.characters = new Map(characters.map((c) => [c.id, c]));
    this.state.update({ status: 'loading', error: undefined });
    let app: PixiNS.Application | null = null;
    try {
      const pixi = await this.load();
      if (this.destroyed) return;
      app = new pixi.Application();
      debugCounters.pixiAppsCreated++;
      const fixed = this.options.exportSize;
      await app.init({
        width: fixed?.width ?? Math.max(1, container.clientWidth),
        height: fixed?.height ?? Math.max(1, container.clientHeight),
        background: fixed ? '#000000' : '#09090b',
        antialias: true,
        autoDensity: !fixed,
        resolution: fixed ? 1 : Math.min(globalThis.devicePixelRatio || 1, this.options.maxResolution ?? 2),
        // Export frames are read from the canvas right after rendering.
        preserveDrawingBuffer: !!fixed,
        preference: 'webgl',
        autoStart: false,
        sharedTicker: false,
      });
      if (this.destroyed) {
        this.destroyApp(app);
        return;
      }
      this.pixi = pixi;
      this.app = app;
      app.canvas.setAttribute('aria-hidden', 'true');
      app.canvas.style.display = 'block';
      app.canvas.dataset.testid = 'animation-canvas';
      container.appendChild(app.canvas);
      app.ticker.add(this.onTick);
      this.buildStage();
      if (this.reactor) this.ensureEffectLayers();
      if (this.visualizer) this.ensureVisualizerLayers();
      if (fixed) for (const g of [this.outside, this.overlay, this.guideGraphic]) if (g) g.visible = false;
      this.syncScene(null);
      if (!fixed) this.observeResize(container);
      this.state.update({ status: 'paused' });
      this.unsubscribeClock = this.clock.subscribe(this.onClock);
      this.onClock();
    } catch (error) {
      if (app && !this.app) this.destroyApp(app);
      const message = error instanceof Error ? error.message : String(error);
      this.state.update({ status: 'error', error: `Animation renderer unavailable: ${message}` });
    }
  }

  setScene(scene: Scene): void {
    const previous = this.scene;
    this.scene = scene;
    if (!this.app) return;
    this.preview.clear();
    this.syncScene(previous);
    this.showSample(this.clock.sample(), true);
    this.render();
  }

  /** The animation to play and display (null shows base properties). */
  setAnimation(animation: Animation | null): void {
    if (animation === this.animation) return;
    this.animation = animation;
    if (!this.app) return;
    this.showSample(this.clock.sample(), true);
    this.render();
  }

  setCharacters(characters: readonly Character[]): void {
    const next = new Map(characters.map((c) => [c.id, c]));
    const changed = [...next.values()].some((c) => this.characters.get(c.id) !== c) || this.characters.size !== next.size;
    this.characters = next;
    if (!changed || !this.app) return;
    this.syncObjects();
    this.render();
  }

  setCamera(camera: Camera): void {
    this.camera = camera;
    if (!this.world) return;
    this.world.position.set(camera.panX, camera.panY);
    this.world.scale.set(camera.zoom);
    this.updateBlurStrengths();
    this.drawOutside();
    this.drawOverlay();
    this.render();
  }

  /** Phase 5B.1: switches glow/shadow/grade filters between full and draft quality. */
  setEffectsQuality(quality: EffectsQuality): void {
    if (quality === this.effectsQuality) return;
    this.effectsQuality = quality;
    for (const b of this.blurs.values()) {
      b.filter.quality = BLUR_QUALITY[quality];
      b.filter.resolution = FILTER_RESOLUTION[quality];
    }
    if (this.gradeFilter) this.gradeFilter.resolution = FILTER_RESOLUTION[quality];
    this.render();
  }

  /**
   * Preview guides (Motion Composer): planned paths and object trajectories. Drawn in the
   * overlay group only; never part of the scene or the project.
   */
  setGuides(guides: readonly Guide[]): void {
    if (guides === this.guides) return;
    this.guides = guides;
    if (!this.app) return;
    this.drawGuides();
    this.render();
  }

  setSelection(ids: readonly string[]): void {
    this.selection = [...ids];
    if (!this.app) return;
    this.drawOverlay();
    this.render();
  }

  /**
   * Temporary transforms while dragging (no project change until pointer-up). Only the
   * affected nodes move; content is scaled rather than redrawn during a resize.
   */
  setPreview(patches: ReadonlyMap<string, Partial<SceneObject>> | null): void {
    const touched = new Set([...this.preview.keys(), ...(patches?.keys() ?? [])]);
    this.preview = new Map(patches ?? []);
    for (const id of touched) {
      const entry = this.nodes.get(id);
      if (entry) this.applyTransform(entry);
    }
    if (this.app) {
      this.drawOverlay();
      this.render();
    }
  }

  /** Playback is owned by the clock; these delegate to it (the renderer follows). */
  start(): void {
    this.clock.play();
  }

  pause(): void {
    this.clock.pause();
  }

  reset(): void {
    this.clock.reset();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.unsubscribeClock?.();
    this.unsubscribeClock = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.app) {
      this.app.ticker.remove(this.onTick);
      // Our display tree first (text textures, gradients, masks), then the app itself.
      this.vizRenderer?.destroy();
      this.vizRenderer = null;
      this.vizBehind = this.vizFront = null;
      this.visualizer = null;
      this.visualizerFrame = null;
      for (const entry of this.nodes.values()) this.releaseNodeResources(entry);
      this.world?.destroy(NODE_DESTROY);
      this.world = null;
      this.sceneRoot = null;
      this.vignette = null;
      this.guideGraphic = null;
      this.flashGraphic = null;
      this.pulseGraphic = null;
      this.reactor = null;
      this.reactions = null;
      this.destroyApp(this.app);
    }
    // Cached textures may not be attached to anything; release them explicitly.
    for (const t of this.textures.values()) this.releaseTexture(t);
    this.textures.clear();
    this.gradient?.destroy();
    this.gradient = null;
    this.vignetteGradient?.destroy();
    this.vignetteGradient = null;
    this.gradeFilter?.destroy();
    this.gradeFilter = null;
    for (const b of this.blurs.values()) b.filter.destroy();
    this.blurs.clear();
    this.app = null;
    this.sprites = [];
    this.nodes.clear();
    this.layerNodes.clear();
    this.state.update({ status: 'destroyed' });
  }

  private destroyApp(app: PixiNS.Application): void {
    try {
      app.ticker?.stop();
      app.destroy({ removeView: true }, { children: true, texture: false, textureSource: false, context: true });
    } finally {
      debugCounters.pixiAppsDestroyed++;
    }
  }

  /** Clock changed (play, pause, seek, reset, timing): start/stop the ticker and redraw. */
  private readonly onClock = (): void => {
    const app = this.app;
    if (!app || this.destroyed) return;
    const playing = this.clock.get().playing;
    if (playing && !app.ticker.started) app.ticker.start();
    if (!playing && app.ticker.started) app.ticker.stop();
    if (this.snapshot.status !== (playing ? 'running' : 'paused')) this.state.update({ status: playing ? 'running' : 'paused' });
    this.showSample(this.clock.sample(), true);
    this.render();
  };

  /** One ticker callback per browser frame while playing; time comes from the clock. */
  private readonly onTick = (): void => {
    const sample = this.clock.sample();
    if (sample.ended) {
      // A one-shot animation reached its end: stop on the last frame.
      this.clock.finish();
      return;
    }
    this.showSample(sample);
  };

  /**
   * Shows the clock's time: Phase 0 shapes move continuously; keyframes are evaluated
   * only when the displayed frame changes (or when forced after a data change).
   */
  private showSample(sample: ClockSample, force = false): void {
    this.shownSeconds = sample.seconds;
    this.layoutAnimated(sample.seconds);
    const frameChanged = force || sample.frame !== this.shownFrame;
    if (frameChanged) {
      this.shownFrame = sample.frame;
      this.evaluate(sample.frame);
      this.state.update({ frame: sample.frame, elapsed: sample.seconds });
      debugCounters.animationFrame = sample.frame;
    }
    if (this.reactor || this.reactions) {
      const t0 = performance.now();
      this.applyReactions(this.reactor?.(sample.seconds) ?? null, frameChanged);
      this.counters.reactionMs += performance.now() - t0;
    }
    if ((this.visualizer || this.visualizerFrame) && this.vizRenderer && this.scene) {
      const t0 = performance.now();
      this.visualizerFrame = this.visualizer?.(sample.seconds) ?? null;
      this.vizRenderer.draw(this.visualizerFrame, this.scene);
      this.counters.visualizerEvaluations++;
      this.counters.visualizerMs += performance.now() - t0;
    }
  }

  /** Applies a reaction frame: transforms of reacting objects (and of those that stopped reacting), then effects. */
  private applyReactions(frame: ReactionFrame | null, transformsDone: boolean): void {
    const previous = this.reactions;
    this.reactions = frame && (frame.objects.size > 0 || frame.effects.flash || frame.effects.background || frame.effects.shake.x || frame.effects.shake.y) ? frame : null;
    if (frame) this.counters.reactionEvaluations++;
    if (previous || this.reactions) {
      const ids = new Set([...(previous?.objects.keys() ?? []), ...(this.reactions?.objects.keys() ?? [])]);
      // evaluate() already re-applied animated objects on a frame change; re-apply the rest.
      for (const id of ids) {
        if (transformsDone && this.evaluated.has(id)) continue;
        const entry = this.nodes.get(id);
        if (entry) this.applyTransform(entry);
      }
      if (ids.size && this.selection.length) this.drawOverlay();
    }
    this.drawEffects();
  }

  private drawEffects(): void {
    const scene = this.scene;
    const fx = this.reactions?.effects;
    if (!scene || !this.flashGraphic || !this.pulseGraphic || !this.world) return;
    const key = fx ? `${fx.flash?.color}:${fx.flash?.alpha.toFixed(3)}|${fx.background?.color}:${fx.background?.alpha.toFixed(3)}` : '';
    if (key !== this.effectKey) {
      this.effectKey = key;
      this.flashGraphic.clear();
      this.pulseGraphic.clear();
      if (fx?.flash) this.flashGraphic.rect(0, 0, scene.width, scene.height).fill({ color: hexToNumber(fx.flash.color), alpha: fx.flash.alpha });
      if (fx?.background) this.pulseGraphic.rect(0, 0, scene.width, scene.height).fill({ color: hexToNumber(fx.background.color), alpha: fx.background.alpha });
    }
    // Camera shake offsets the whole world (scene pixels scaled by zoom); never the project.
    const sx = (fx?.shake.x ?? 0) * this.camera.zoom;
    const sy = (fx?.shake.y ?? 0) * this.camera.zoom;
    this.world.position.set(this.camera.panX + sx, this.camera.panY + sy);
  }

  private evaluate(frame: number): void {
    const previous = this.evaluated;
    if (this.animation && this.animation.sceneId === this.scene?.id) {
      const t0 = performance.now();
      this.evaluated = evaluateAnimation(this.animation, frame);
      this.counters.evaluationMs += performance.now() - t0;
      this.counters.evaluations++;
    } else this.evaluated = new Map();
    if (previous.size === 0 && this.evaluated.size === 0) return;
    for (const id of new Set([...previous.keys(), ...this.evaluated.keys()])) {
      const entry = this.nodes.get(id);
      if (entry) this.applyTransform(entry);
    }
    this.drawOverlay();
  }

  private observeResize(container: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box || !this.app) return;
      const width = Math.max(1, Math.floor(box.width));
      const height = Math.max(1, Math.floor(box.height));
      this.app.renderer.resize(width, height);
      this.options.onResize?.(width, height);
      this.drawOutside();
      this.render();
    });
    this.resizeObserver.observe(container);
  }

  // ------------------------------------------------------------------ Stage

  private buildStage(): void {
    const pixi = this.pixi!;
    const app = this.app!;
    this.world = new pixi.Container();
    this.sceneRoot = new pixi.Container();
    this.vignette = new pixi.Graphics();
    this.vignette.visible = false;
    this.backdrop = new pixi.Graphics();
    // Separate render groups: the overlay is redrawn on every selection/drag change and the
    // Phase 0 shapes move every frame; isolating them keeps Pixi from rebuilding and
    // re-uploading the (large, batched) scene geometry on each of those renders.
    this.layersRoot = new pixi.Container({ isRenderGroup: true });
    this.animated = new pixi.Container({ isRenderGroup: true });
    this.outside = new pixi.Graphics();
    this.overlay = new pixi.Graphics();
    this.overlay.isRenderGroup = true;
    this.guideGraphic = new pixi.Graphics();
    this.guideGraphic.isRenderGroup = true;
    // sceneRoot holds what the colour grade applies to; the vignette sits above it, then the
    // editor chrome. With a neutral look this draws exactly what earlier versions drew.
    this.sceneRoot.addChild(this.backdrop, this.layersRoot, this.animated);
    this.world.addChild(this.sceneRoot, this.vignette, this.outside, this.guideGraphic, this.overlay);
    app.stage.addChild(this.world);
    this.world.position.set(this.camera.panX, this.camera.panY);
    this.world.scale.set(this.camera.zoom);
  }

  private syncScene(previous: Scene | null): void {
    const scene = this.scene;
    if (!scene) return;
    if (!previous || previous.background !== scene.background || previous.gradient !== scene.gradient || previous.width !== scene.width || previous.height !== scene.height) {
      this.drawBackdrop();
      this.drawOutside();
    }
    if (!previous || previous.backgroundImage !== scene.backgroundImage || previous.width !== scene.width || previous.height !== scene.height) this.drawBackgroundImage();
    if (!previous || previous.look !== scene.look || previous.width !== scene.width || previous.height !== scene.height) this.applyLook();
    if (!previous || previous.elements !== scene.elements) this.buildAnimated();
    if (!previous || previous.layers !== scene.layers || previous.objects !== scene.objects || previous.id !== scene.id) this.syncObjects();
    this.drawOverlay();
    this.evictTextures();
  }

  private drawBackdrop(): void {
    const pixi = this.pixi!;
    const scene = this.scene!;
    const g = this.backdrop!;
    g.clear();
    this.gradient?.destroy();
    this.gradient = null;
    if (scene.gradient) {
      const a = (scene.gradient.angle * Math.PI) / 180;
      const dx = Math.cos(a) / 2;
      const dy = Math.sin(a) / 2;
      this.gradient = new pixi.FillGradient({
        type: 'linear',
        start: { x: 0.5 - dx, y: 0.5 - dy },
        end: { x: 0.5 + dx, y: 0.5 + dy },
        colorStops: [
          { offset: 0, color: scene.background },
          { offset: 1, color: scene.gradient.to },
        ],
        textureSpace: 'local',
      });
      g.rect(0, 0, scene.width, scene.height).fill(this.gradient);
    } else {
      g.rect(0, 0, scene.width, scene.height).fill(hexToNumber(scene.background));
    }
  }

  /** Dims everything outside the scene canvas so the frame is obvious while editing. */
  private drawOutside(): void {
    const scene = this.scene;
    const g = this.outside;
    if (!g || !scene) return;
    const far = 40000;
    g.clear();
    g.rect(-far, -far, far * 2 + scene.width, far).fill({ color: 0x09090b, alpha: 0.72 });
    g.rect(-far, scene.height, far * 2 + scene.width, far).fill({ color: 0x09090b, alpha: 0.72 });
    g.rect(-far, 0, far, scene.height).fill({ color: 0x09090b, alpha: 0.72 });
    g.rect(scene.width, 0, far, scene.height).fill({ color: 0x09090b, alpha: 0.72 });
    g.rect(0, 0, scene.width, scene.height).stroke({ width: 1 / this.camera.zoom, color: 0x52525b });
  }

  private drawBackgroundImage(): void {
    const pixi = this.pixi!;
    const scene = this.scene!;
    if (this.backgroundSprite) {
      this.backgroundSprite.destroy({ texture: false });
      this.backgroundSprite = null;
    }
    if (this.backgroundMask) {
      this.backgroundMask.destroy();
      this.backgroundMask = null;
    }
    const bg = scene.backgroundImage;
    if (!bg) return;
    const cached = this.textures.get(bg.assetId);
    if (!cached) {
      this.requestTexture(bg.assetId);
      return;
    }
    const sprite = new pixi.Sprite(cached.texture);
    const iw = cached.texture.width;
    const ih = cached.texture.height;
    let w = scene.width;
    let h = scene.height;
    if (bg.fit === 'cover' || bg.fit === 'contain') {
      const s = (bg.fit === 'cover' ? Math.max : Math.min)(scene.width / iw, scene.height / ih);
      w = iw * s;
      h = ih * s;
    } else if (bg.fit === 'center') {
      w = iw;
      h = ih;
    }
    w *= bg.scale;
    h *= bg.scale;
    sprite.width = w;
    sprite.height = h;
    sprite.position.set((scene.width - w) / 2 + bg.offsetX, (scene.height - h) / 2 + bg.offsetY);
    sprite.alpha = bg.opacity;
    const mask = new pixi.Graphics().rect(0, 0, scene.width, scene.height).fill(0xffffff);
    sprite.mask = mask;
    this.backdrop!.parent!.addChildAt(sprite, 1);
    this.backdrop!.parent!.addChildAt(mask, 2);
    this.backgroundSprite = sprite;
    this.backgroundMask = mask;
  }

  // ------------------------------------------------------------------ Objects

  private syncObjects(): void {
    const pixi = this.pixi;
    const scene = this.scene;
    if (!pixi || !scene || !this.layersRoot) return;
    // Layers: one container each, in saved order (bottom → top).
    const wantedLayers = new Set(scene.layers.map((l) => l.id));
    for (const [id, c] of this.layerNodes) {
      if (!wantedLayers.has(id)) {
        c.removeChildren();
        c.destroy();
        this.layerNodes.delete(id);
      }
    }
    for (const layer of scene.layers) {
      let c = this.layerNodes.get(layer.id);
      if (!c) {
        c = new pixi.Container();
        this.layerNodes.set(layer.id, c);
        this.layerBaseBlend.set(c, c.blendMode);
      }
      c.visible = layer.visible;
      // Phase 5B.1: layer opacity multiplies each object's opacity (no extra render pass);
      // the blend mode is a GPU blend state. Absent values keep the previous rendering.
      c.alpha = layer.opacity ?? 1;
      c.blendMode = layer.blend && layer.blend !== 'normal' ? layer.blend : (this.layerBaseBlend.get(c) ?? c.blendMode);
    }
    // Re-parent only when the layer order changed: rebuilding the display tree makes Pixi
    // rebuild (and re-upload) every batch, which dominated edit cost in large scenes.
    const layerOrder = scene.layers.map((l) => this.layerNodes.get(l.id)!);
    if (!sameChildren(this.layersRoot, layerOrder)) {
      this.layersRoot.removeChildren();
      for (const c of layerOrder) this.layersRoot.addChild(c);
    }

    // Objects: create / update / destroy by id.
    const wanted = new Set(scene.objects.map((o) => o.id));
    for (const [id, entry] of this.nodes) {
      if (!wanted.has(id)) {
        this.releaseNodeResources(entry);
        entry.node.destroy(NODE_DESTROY);
        this.nodes.delete(id);
      }
    }
    const wantedChildren = new Map<string, PixiNS.Container[]>(scene.layers.map((l) => [l.id, []]));
    for (const object of scene.objects) {
      let entry = this.nodes.get(object.id);
      const drawKey = drawKeyOf(object, this.characters);
      if (!entry) {
        const node = new pixi.Container();
        const content = new pixi.Container();
        node.addChild(content);
        entry = { object, drawKey: '', node, content };
        this.nodes.set(object.id, entry);
      }
      if (entry.drawKey !== drawKey) {
        entry.object = object;
        this.drawContent(entry, !!entry.segments || !isNeutralPose(this.shown({ ...entry, object }).pose));
        entry.drawKey = drawKey;
      } else if (entry.object !== object) {
        this.counters.transformUpdates++;
      }
      entry.object = object;
      this.applyTransform(entry);
      wantedChildren.get(object.layerId)?.push(entry.node);
    }
    for (const [layerId, children] of wantedChildren) {
      const layer = this.layerNodes.get(layerId)!;
      if (sameChildren(layer, children)) continue;
      layer.removeChildren();
      for (const c of children) layer.addChild(c);
    }
  }

  /** Base object + animated values (at the displayed frame) + drag preview. */
  private shown(entry: ObjectNode): DisplayObject {
    const values = this.evaluated.get(entry.object.id);
    const reaction = this.reactions?.objects.get(entry.object.id);
    const animated = displayObject(entry.object, reaction ? composeObject(entry.object, values, reaction) : values);
    const preview = this.preview.get(entry.object.id);
    return preview ? ({ ...animated, ...preview } as DisplayObject) : animated;
  }

  private applyTransform(entry: ObjectNode): void {
    const o = this.shown(entry);
    // First time a character needs a pose: switch it to segment graphics (one redraw).
    if (entry.object.type === 'character' && !entry.segments && !isNeutralPose(o.pose) && this.characters.has(entry.object.characterId)) this.drawContent(entry, true);
    const scene = this.scene!;
    const { node, content } = entry;
    node.pivot.set(o.anchorX * o.width, o.anchorY * o.height);
    node.position.set(o.x + o.anchorX * o.width, o.y + o.anchorY * o.height);
    node.rotation = (o.rotation * Math.PI) / 180;
    node.alpha = o.opacity;
    node.visible = isShown(scene, o);
    // Tint only nodes that react (or just stopped): editors never touch it.
    const tint = this.reactions?.objects.get(entry.object.id)?.tint;
    if (tint || entry.tinted) {
      node.tint = tint ? mixWhite(hexToNumber(tint.color), tint.strength) : 0xffffff;
      entry.tinted = !!tint;
    }
    // While resizing (or scaling by animation), scale the existing drawing instead of redrawing.
    const drawn = entry.object;
    content.scale.set(o.width / drawn.width, o.height / drawn.height);
    if (entry.effects) {
      // Copies scale with the drawing; a shadow's offset stays in scene space whatever the
      // object's rotation (light does not turn with the object).
      const a = -node.rotation;
      for (const fx of entry.effects) {
        fx.container.scale.set(content.scale.x, content.scale.y);
        fx.container.position.set(fx.offsetX * Math.cos(a) - fx.offsetY * Math.sin(a), fx.offsetX * Math.sin(a) + fx.offsetY * Math.cos(a));
      }
    }
    if (entry.segments) this.applyPose(entry, o.pose ?? NEUTRAL_POSE);
  }

  /** Poses a character's segment graphics (rigid transforms in drawn pixels; no redraw). */
  private applyPose(entry: ObjectNode, pose: CharacterPose): void {
    const key = `${pose.head},${pose.torso},${pose.armL},${pose.armR},${pose.legL},${pose.legR},${pose.headX},${pose.headY},${pose.torsoX},${pose.torsoY}`;
    if (key === entry.poseKey || !entry.rig || !entry.segments) return;
    entry.poseKey = key;
    this.counters.poseUpdates++;
    const t = segmentTransforms(entry.rig, pose, entry.object.width, entry.object.height);
    const lists = [entry.segments, ...(entry.effects ?? []).map((fx) => fx.segments ?? [])];
    for (const list of lists) {
      for (const { segment, graphic } of list) {
        const s = t[segment];
        graphic.pivot.set(s.pivotX, s.pivotY);
        graphic.position.set(s.x, s.y);
        graphic.rotation = s.rotation;
      }
    }
  }

  private drawContent(entry: ObjectNode, rigged = false): void {
    const { content } = entry;
    const o = entry.object;
    for (const child of content.removeChildren()) child.destroy(NODE_DESTROY);
    this.releaseNodeResources(entry);
    entry.segments = undefined;
    entry.rig = undefined;
    entry.poseKey = undefined;
    this.counters.redraws++;
    content.scale.set(1);
    content.position.set(0, 0);
    const drawn = this.buildDrawing(entry, content, o, rigged, null);
    if (drawn.segments) {
      entry.segments = drawn.segments;
      entry.rig = drawn.rig;
    }
    // Phase 5B.1: glow and shadow are solid-colour copies of the same drawing, behind it.
    if (drawn.real && (o.shadow || o.glow)) {
      entry.effects = [];
      if (o.shadow && o.shadow.opacity > 0) entry.effects.push(this.buildEffect(entry, o, rigged, 'shadow', o.shadow.color, o.shadow.blur, o.shadow.opacity, o.shadow.offsetX, o.shadow.offsetY));
      if (o.glow && o.glow.strength > 0) entry.effects.push(this.buildEffect(entry, o, rigged, 'glow', o.glow.color, o.glow.radius, o.glow.strength, 0, 0));
      // Back to front: shadow, glow, then the object itself.
      entry.effects.forEach((fx, i) => entry.node.addChildAt(fx.container, i));
    }
  }

  /** Builds an effect copy: the drawing in one colour, blurred by a shared filter. */
  private buildEffect(entry: ObjectNode, o: SceneObject, rigged: boolean, kind: EffectCopy['kind'], color: string, radius: number, alpha: number, offsetX: number, offsetY: number): EffectCopy {
    const container = new this.pixi!.Container();
    const drawn = this.buildDrawing(entry, container, o, rigged, hexToNumber(color));
    container.alpha = alpha;
    if (kind === 'glow') container.blendMode = 'add';
    const blurKey = radius > 0 ? this.acquireBlur(radius) : null;
    if (blurKey) container.filters = [this.blurs.get(blurKey)!.filter];
    return { kind, container, blurKey, segments: drawn.segments, offsetX, offsetY };
  }

  /** Effect copies and gradients a node owns: shared blurs are released, gradients destroyed. */
  private releaseNodeResources(entry: ObjectNode): void {
    for (const fx of entry.effects ?? []) {
      fx.container.filters = null;
      fx.container.destroy(NODE_DESTROY);
      if (fx.blurKey) this.releaseBlur(fx.blurKey);
    }
    entry.effects = undefined;
    for (const g of entry.gradients ?? []) g.destroy();
    entry.gradients = undefined;
  }

  private blurStrength(radius: number): number {
    // Radii are in scene pixels; filters run in screen pixels, so scale by the camera zoom.
    return radius * this.camera.zoom;
  }

  private acquireBlur(radius: number): string {
    const key = `${radius}`;
    const shared = this.blurs.get(key);
    if (shared) {
      shared.refs++;
      return key;
    }
    const filter = new this.pixi!.BlurFilter({ strength: this.blurStrength(radius), quality: BLUR_QUALITY[this.effectsQuality], resolution: FILTER_RESOLUTION[this.effectsQuality] });
    this.blurs.set(key, { filter, radius, refs: 1 });
    return key;
  }

  private releaseBlur(key: string): void {
    const shared = this.blurs.get(key);
    if (!shared) return;
    if (--shared.refs > 0) return;
    shared.filter.destroy();
    this.blurs.delete(key);
  }

  private updateBlurStrengths(): void {
    for (const b of this.blurs.values()) b.filter.strength = this.blurStrength(b.radius);
  }

  /**
   * Draws an object into `target`. With `solid`, every fill and stroke uses that colour (the
   * silhouette used for glow and shadow); images are tinted. Returns the posable segments of
   * a rigged character and whether a real drawing (not a placeholder) was made.
   */
  private buildDrawing(entry: ObjectNode, target: PixiNS.Container, o: SceneObject, rigged: boolean, solid: number | null): { segments?: { segment: Segment; graphic: PixiNS.Graphics }[]; rig?: Rig; real: boolean } {
    const pixi = this.pixi!;
    switch (o.type) {
      case 'rect':
      case 'circle':
      case 'ellipse':
      case 'polygon': {
        const g = new pixi.Graphics();
        if (o.type === 'rect') {
          if (o.cornerRadius > 0) g.roundRect(0, 0, o.width, o.height, Math.min(o.cornerRadius, o.width / 2, o.height / 2));
          else g.rect(0, 0, o.width, o.height);
        } else if (o.type === 'polygon') {
          g.poly(o.points.flatMap(([x, y]) => [x * o.width, y * o.height]));
        } else {
          g.ellipse(o.width / 2, o.height / 2, o.width / 2, o.height / 2);
        }
        if (solid !== null) g.fill(solid);
        else if (o.style.gradient) g.fill(this.fillGradient(entry, o.style.gradient));
        else g.fill(hexToNumber(o.style.fill));
        if (o.style.stroke && o.style.stroke.width > 0) g.stroke({ width: o.style.stroke.width, color: solid ?? hexToNumber(o.style.stroke.color), alignment: 1 });
        target.addChild(g);
        return { real: true };
      }
      case 'text': {
        const text = new pixi.Text({
          text: o.text,
          style: {
            fontFamily: FONTS[o.fontFamily] ?? FONTS.sans!,
            fontSize: o.fontSize,
            fontWeight: o.bold ? '700' : '400',
            fill: solid ?? hexToNumber(o.color),
            align: o.align,
            wordWrap: true,
            wordWrapWidth: Math.max(1, o.width),
          },
        });
        const ax = o.align === 'left' ? 0 : o.align === 'center' ? 0.5 : 1;
        text.anchor.set(ax, 0.5);
        text.position.set(ax * o.width, o.height / 2);
        target.addChild(text);
        return { real: true };
      }
      case 'image': {
        const cached = this.textures.get(o.assetId);
        if (!cached) {
          if (solid === null) {
            target.addChild(this.placeholder(o.width, o.height, this.imageErrors.has(o.assetId)));
            this.requestTexture(o.assetId);
          }
          return { real: false };
        }
        const sprite = new pixi.Sprite(cached.texture);
        if (solid !== null) sprite.tint = solid;
        const iw = cached.texture.width;
        const ih = cached.texture.height;
        if (o.fit === 'stretch') {
          sprite.width = o.width;
          sprite.height = o.height;
        } else {
          const s = (o.fit === 'cover' ? Math.max : Math.min)(o.width / iw, o.height / ih);
          sprite.width = iw * s;
          sprite.height = ih * s;
          sprite.position.set((o.width - iw * s) / 2, (o.height - ih * s) / 2);
          if (o.fit === 'cover') {
            const mask = new pixi.Graphics().rect(0, 0, o.width, o.height).fill(0xffffff);
            target.addChild(mask);
            sprite.mask = mask;
          }
        }
        target.addChild(sprite);
        return { real: true };
      }
      case 'character': {
        const def = this.characters.get(o.characterId);
        if (!def) {
          if (solid === null) target.addChild(this.placeholder(o.width, o.height, true));
          return { real: false };
        }
        const W = o.width;
        const H = o.height;
        // Unposed characters are one Graphics (cheapest to batch). Once a character needs a
        // pose it is drawn as one Graphics per run of same-segment parts, inside a body that
        // carries the flip; poses then only move those graphics (see applyPose).
        const body = new pixi.Container();
        const runs = rigged ? segmentRuns(characterParts(def)) : [{ segment: 'torso' as Segment, parts: characterParts(def) }];
        const graphics = runs.map((run) => {
          const g = new pixi.Graphics();
          for (const p of run.parts) {
            const color = solid ?? hexToNumber(p.color);
            if (p.kind === 'rect') {
              if (p.radius && p.radius > 0) g.roundRect(p.x * W, p.y * H, p.w * W, p.h * H, Math.min(p.radius * W, (p.w * W) / 2, (p.h * H) / 2));
              else g.rect(p.x * W, p.y * H, p.w * W, p.h * H);
            } else if (p.kind === 'ellipse') {
              g.ellipse(p.cx * W, p.cy * H, p.rx * W, p.ry * H);
            } else {
              g.poly(p.points.map((v, i) => v * (i % 2 === 0 ? W : H)));
            }
            g.fill(color);
          }
          body.addChild(g);
          return { segment: run.segment, graphic: g };
        });
        if (o.flipX) {
          body.scale.x = -1;
          body.position.x = W;
        }
        target.addChild(body);
        return rigged ? { segments: graphics, rig: characterRig(def), real: true } : { real: true };
      }
    }
  }

  /** A gradient fill in the shape's own box (destroyed with the node's drawing). */
  private fillGradient(entry: ObjectNode, g: FillGradient): PixiNS.FillGradient {
    const colorStops = g.stops.map((s) => ({ offset: s.offset, color: stopColor(s) }));
    const fill =
      g.type === 'linear'
        ? new this.pixi!.FillGradient({ type: 'linear', ...linearPoints(g.angle), colorStops, textureSpace: 'local' })
        : this.radialGradient(g.cx, g.cy, g.radius, colorStops);
    (entry.gradients ??= []).push(fill);
    return fill;
  }

  /**
   * A radial gradient in the unit box. Pixi 8.22 builds its radial texture by first filling
   * it with the last stop's colour and then drawing the gradient over it, so wherever an
   * earlier stop is more transparent than the last one, the last colour shows through (a
   * vignette — clear centre, dark edge — would darken everything). The texture is built here
   * the same way minus that pre-fill: a canvas radial gradient already continues the last
   * stop beyond its outer circle. Pixi then uses this texture as is (it only builds one when
   * none is set).
   */
  private radialGradient(cx: number, cy: number, radius: number, colorStops: { offset: number; color: string }[]): PixiNS.FillGradient {
    const pixi = this.pixi!;
    const fill = new pixi.FillGradient({ type: 'radial', center: { x: cx, y: cy }, innerRadius: 0, outerCenter: { x: cx, y: cy }, outerRadius: radius, colorStops, textureSpace: 'local' });
    const size = RADIAL_TEXTURE_SIZE;
    const canvas = pixi.DOMAdapter.get().createCanvas(size, size);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
    if (!ctx) return fill; // no 2D canvas: Pixi builds its own texture
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    for (const stop of fill.colorStops) gradient.addColorStop(stop.offset, stop.color as string);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    fill.texture = new pixi.Texture({ source: new pixi.ImageSource({ resource: canvas, addressMode: 'clamp-to-edge' }) });
    // The texture spans the outer circle's bounding square, in box units.
    fill.transform = new pixi.Matrix().scale(2 * radius, 2 * radius).translate(cx - radius, cy - radius);
    return fill;
  }

  /** Phase 5B.1: the scene's colour grade (one filter on sceneRoot) and vignette (a gradient above it). */
  private applyLook(): void {
    const pixi = this.pixi;
    const scene = this.scene;
    const root = this.sceneRoot;
    const v = this.vignette;
    if (!pixi || !scene || !root || !v) return;
    const grade = scene.look?.grade;
    if (grade && !isNeutralGrade(grade)) {
      // 'inherit': the graded scene keeps the canvas's anti-aliasing (Pixi filters default to
      // none, which makes every edge under the grade jagged and sub-pixel dependent).
      this.gradeFilter ??= new pixi.ColorMatrixFilter({ resolution: FILTER_RESOLUTION[this.effectsQuality], antialias: 'inherit' });
      this.gradeFilter.matrix = gradeMatrix(grade) as PixiNS.ColorMatrix;
      root.filters = [this.gradeFilter];
    } else if (this.gradeFilter) {
      // A neutral grade attaches no filter at all: no extra pass, identical pixels.
      root.filters = null;
      this.gradeFilter.destroy();
      this.gradeFilter = null;
    }
    v.clear();
    this.vignetteGradient?.destroy();
    this.vignetteGradient = null;
    const vig = scene.look?.vignette;
    v.visible = isVisibleVignette(vig);
    if (isVisibleVignette(vig)) {
      this.vignetteGradient = this.radialGradient(0.5, 0.5, VIGNETTE_RADIUS, vignetteStops(vig));
      v.rect(0, 0, scene.width, scene.height).fill(this.vignetteGradient);
    }
  }

  private placeholder(w: number, h: number, failed: boolean): PixiNS.Graphics {
    const g = new this.pixi!.Graphics();
    g.rect(0, 0, w, h).fill({ color: failed ? 0x7f1d1d : 0x3f3f46, alpha: 0.8 });
    g.moveTo(0, 0).lineTo(w, h).moveTo(w, 0).lineTo(0, h).stroke({ width: 2, color: failed ? 0xfca5a5 : 0xa1a1aa });
    return g;
  }

  // ------------------------------------------------------------------ Textures

  private requestTexture(assetId: string): void {
    const loader = this.options.loadImage;
    if (!loader || this.loading.has(assetId) || this.imageErrors.has(assetId)) return;
    const promise = loader(assetId).then(
      (bitmap) => {
        this.loading.delete(assetId);
        if (this.destroyed || !this.pixi) {
          bitmap.close?.();
          return;
        }
        const texture = this.pixi.Texture.from(bitmap);
        this.textures.set(assetId, { texture, bitmap, bytes: bitmap.width * bitmap.height * 4 });
        this.refreshImages(assetId);
      },
      () => {
        this.loading.delete(assetId);
        if (this.destroyed) return;
        this.imageErrors.add(assetId);
        this.state.update({ imageErrors: [...this.imageErrors] });
        this.refreshImages(assetId);
      },
    );
    this.loading.set(assetId, promise);
  }

  /** Redraws objects/background that were waiting for an image. */
  private refreshImages(assetId: string): void {
    if (!this.app || !this.scene) return;
    for (const entry of this.nodes.values()) {
      if (entry.object.type === 'image' && entry.object.assetId === assetId) {
        this.drawContent(entry);
        this.applyTransform(entry);
      }
    }
    if (this.scene.backgroundImage?.assetId === assetId) this.drawBackgroundImage();
    this.evictTextures();
    this.render();
  }

  /** Keeps decoded textures within budget, never evicting ones the current scene uses. */
  private evictTextures(): void {
    const budget = this.options.textureBudgetBytes ?? DEFAULT_TEXTURE_BUDGET;
    const scene = this.scene;
    if (!scene) return;
    const inUse = new Set<string>(scene.objects.filter((o) => o.type === 'image').map((o) => (o as { assetId: string }).assetId));
    if (scene.backgroundImage) inUse.add(scene.backgroundImage.assetId);
    let total = 0;
    for (const t of this.textures.values()) total += t.bytes;
    for (const [id, t] of this.textures) {
      if (total <= budget) break;
      if (inUse.has(id)) continue;
      this.releaseTexture(t);
      this.textures.delete(id);
      total -= t.bytes;
    }
  }

  private releaseTexture(t: CachedTexture): void {
    t.texture.destroy(true);
    t.bitmap.close?.();
  }

  // ------------------------------------------------------------------ Overlay

  private drawOverlay(): void {
    const g = this.overlay;
    const scene = this.scene;
    if (!g || !scene) return;
    g.clear();
    const z = this.camera.zoom;
    const line = 1.5 / z;
    const size = 8 / z;
    for (const id of this.selection) {
      const entry = this.nodes.get(id);
      if (!entry) continue;
      const o = this.shown(entry);
      const locked = isLocked(scene, o);
      const color = locked ? 0xf59e0b : 0x38bdf8;
      const pts = cornersOf(o);
      g.poly(pts.flatMap((p) => [p.x, p.y])).stroke({ width: line, color });
      if (locked || this.selection.length > 1) continue;
      const top = localToScene(o, { x: o.width / 2, y: 0 });
      const rot = localToScene(o, { x: o.width / 2, y: -ROTATE_OFFSET / z });
      g.moveTo(top.x, top.y).lineTo(rot.x, rot.y).stroke({ width: line, color });
      g.circle(rot.x, rot.y, size * 0.7).fill(0xffffff).stroke({ width: line, color });
      for (const [fx, fy] of Object.values(HANDLE_FRACTIONS)) {
        const p = localToScene(o, { x: fx * o.width, y: fy * o.height });
        g.rect(p.x - size / 2, p.y - size / 2, size, size).fill(0xffffff).stroke({ width: line, color });
      }
    }
  }

  private drawGuides(): void {
    const g = this.guideGraphic;
    if (!g) return;
    g.clear();
    const z = this.camera.zoom;
    for (const guide of this.guides) {
      const pts = guide.points;
      if (pts.length < 2) continue;
      const color = guide.kind === 'path' ? 0xf472b6 : 0x38bdf8;
      g.moveTo(pts[0]![0], pts[0]![1]);
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i]![0], pts[i]![1]);
      g.stroke({ width: (guide.kind === 'path' ? 2.5 : 1.5) / z, color, alpha: guide.kind === 'path' ? 0.95 : 0.75 });
      // Start (filled) and end (ring) markers.
      g.circle(pts[0]![0], pts[0]![1], 5 / z).fill({ color, alpha: 0.9 });
      g.circle(pts[pts.length - 1]![0], pts[pts.length - 1]![1], 5 / z).stroke({ width: 2 / z, color });
    }
  }

  // ------------------------------------------------------------------ Phase 0 animation

  private buildAnimated(): void {
    const pixi = this.pixi!;
    const scene = this.scene!;
    for (const sprite of this.sprites) sprite.graphic.destroy();
    this.sprites = scene.elements.map((element) => {
      const g = new pixi.Graphics();
      const color = hexToNumber(element.color);
      // Unit-sized geometry; scaled per frame from the element's pose.
      if (element.shape === 'circle') g.circle(0, 0, 1).fill(color);
      else if (element.shape === 'square') g.rect(-1, -1, 2, 2).fill(color);
      else g.poly([0, -1.15, 1, 0.6, -1, 0.6]).fill(color);
      this.animated!.addChild(g);
      return { element, graphic: g };
    });
    this.layoutAnimated();
  }

  private layoutAnimated(seconds = this.clock.seconds()): void {
    const scene = this.scene;
    if (!scene) return;
    const t = seconds * scene.speed;
    for (const { element, graphic } of this.sprites) {
      const pose = poseAt(element, t, scene.width, scene.height);
      graphic.position.set(pose.x, pose.y);
      graphic.scale.set(pose.scale);
      graphic.rotation = pose.rotation;
      graphic.alpha = pose.alpha;
    }
  }

  /** Draws a single frame (used after changes, while paused, after reset and resize). */
  private render(): void {
    if (!this.app) return;
    this.app.render();
  }
}

